import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default from 'react';
import { Box } from '../Box/index.js';
import { Flex } from '../Flex/index.js';
import { Icon } from '../Icon/index.js';
import { Stack } from '../Stack/index.js';
import { defaultGetAriaLabel } from './helpers.js';
import { StyledPaginationButton } from './styles.js';
import usePagination from './usePagination.js';

var PageType;
(function (PageType) {
  PageType["NUMBERED_PAGE"] = "page";
  PageType["NEXT_PAGE"] = "next";
  PageType["PREVIOUS_PAGE"] = "previous";
  PageType["START_ELLIPSIS"] = "start-ellipsis";
  PageType["END_ELLIPSIS"] = "end-ellipsis";
})(PageType || (PageType = {}));
/* ======================================= */
/**
 * The Pagination component enables the user to select a
 * specific page from a range of pages.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/pagination#usage)
 * - [Props](https://blockparty.endpoint.com/components/pagination#props)
 * <br />
 *
 */
const Pagination = _ref => {
  let {
    children,
    boundaryCount,
    count,
    dataTestId = 'pagination',
    defaultPage,
    hideNextButton,
    hidePrevButton,
    onChange,
    page,
    siblingCount,
    disabled,
    ...props
  } = _ref;
  const {
    items
  } = usePagination({
    count,
    boundaryCount,
    defaultPage,
    hideNextButton,
    hidePrevButton,
    disabled,
    onChange,
    page,
    siblingCount
  });
  return /*#__PURE__*/React__default.createElement(Box, _extends({
    "aria-label": "pagination navigation",
    as: "nav",
    "data-test-id": dataTestId
  }, props), /*#__PURE__*/React__default.createElement(Stack, {
    alignItems: "center",
    as: "ul",
    direction: "row",
    display: "flex",
    flexWrap: "wrap",
    listStylePosition: "unset",
    listStyleType: "none",
    spacing: "space0"
  }, items.map((item, index) => {
    const paginatedItem = getPaginationItem(item);
    return /*#__PURE__*/React__default.createElement(Flex, {
      key: index,
      alignItems: "center",
      as: "li",
      justifyContent: "center",
      size: 40
    }, paginatedItem);
  })));
};
const getPaginationItem = item => {
  switch (item.type) {
    case PageType.START_ELLIPSIS:
    case PageType.END_ELLIPSIS:
      return '…';
    case PageType.PREVIOUS_PAGE:
    case PageType.NEXT_PAGE:
      return /*#__PURE__*/React__default.createElement(PaginationItem, item, /*#__PURE__*/React__default.createElement(Icon, {
        name: item.type === PageType.PREVIOUS_PAGE ? 'ArrowChevronLeft' : 'ArrowChevronRight',
        size: "large"
      }));
    case PageType.NUMBERED_PAGE:
    default:
      return /*#__PURE__*/React__default.createElement(PaginationItem, item);
  }
};
const PaginationItem = _ref2 => {
  let {
    page,
    selected,
    type,
    children,
    ...item
  } = _ref2;
  const ariaLabel = defaultGetAriaLabel({
    type,
    page,
    selected
  });
  return (
    /*#__PURE__*/
    // @ts-ignore
    React__default.createElement(StyledPaginationButton, _extends({
      _focusVisible: {
        boxShadow: 'focus'
      },
      "aria-label": ariaLabel,
      as: "button",
      "data-test-id": "pagination-button",
      selected: selected
    }, item), children || page)
  );
};
Pagination.displayName = 'Pagination';
PaginationItem.displayName = 'PaginationItem';

export { Pagination, getPaginationItem };
